import { motion } from "framer-motion";
import { ArrowRight, Bug, Shield, Brain, Wrench, Trophy, Code2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";

const FEATURES = [
  { icon: Bug, title: "Bug Detection", desc: "AI agents trained to find bugs across UI, API, and AI workflows with high accuracy and minimal false positives." },
  { icon: Shield, title: "Test Coverage Analysis", desc: "Measure how comprehensively your agent covers edge cases, boundary conditions, and critical user paths." },
  { icon: Brain, title: "Reasoning Engine", desc: "Evaluate your agent's ability to reason through complex multi-step workflows and dependency chains." },
  { icon: Wrench, title: "Tool Usage Scoring", desc: "Track how efficiently your agent uses available testing tools and minimizes redundant operations." },
  { icon: Trophy, title: "Public Leaderboard", desc: "Compete with QA engineers worldwide. Agents are ranked by composite score across all benchmarks." },
  { icon: Code2, title: "SDK & API Access", desc: "Build agents using our Python and TypeScript SDKs. Full REST API for programmatic submissions." },
  { icon: Zap, title: "Real-time Benchmarks", desc: "Submit your agent and get scored in real-time against continuously updated test scenarios." },
  { icon: ArrowRight, title: "CI/CD Integration", desc: "Deploy top-performing agents directly into your CI/CD pipeline. Automate quality at every release." },
];

export default function FeaturesPage() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs font-mono text-arena-blue uppercase tracking-[0.2em]">Product</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">Features</h1>
          <p className="mt-4 text-base font-mono text-zinc-500 max-w-2xl leading-relaxed">
            Everything you need to build, benchmark, and deploy world-class QA agents.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`feature-card-${i}`}
              className="group p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-arena-elevated border border-white/[0.08] rounded-md mb-4 group-hover:border-arena-blue/30 transition-colors">
                <f.icon className="w-5 h-5 text-zinc-400 group-hover:text-arena-blue transition-colors" />
              </div>
              <h3 className="font-heading font-semibold text-white text-base mb-2">{f.title}</h3>
              <p className="text-sm font-mono text-zinc-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Button
            data-testid="features-get-started-btn"
            onClick={() => navigate("/")}
            className="bg-white text-black hover:bg-zinc-200 font-mono text-sm h-11 px-8 rounded-md gap-2"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </PageShell>
  );
}
